import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { BarChart3, ChevronLeft, ChevronRight, Download, RefreshCw, Search } from 'lucide-react'
import { Roles } from '../auth/rbac'
import { useAuth } from '../auth/AuthContext'
import OperatorOrgContractSelector, { normalizeActiveOrgContracts } from '../components/OperatorOrgContractSelector'
import { api, getApiErrorMessage } from '../lib/api'

const CHART_COLORS = ['#0f766e', '#2563eb', '#d97706', '#7c3aed', '#be123c', '#0891b2']
const MEMBER_PAGE_SIZE = 10
const ORG_DASHBOARD_TABS = [
  { key: 'dashboard', label: '대시보드' },
  { key: 'stats', label: '통계' },
  { key: 'details', label: '상세현황' },
]

export default function OrgDashboardPage() {
  const { hasAnyRole } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const isOperator = Boolean(hasAnyRole?.([Roles.OPERATOR]))
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [orgBilling, setOrgBilling] = useState(null)
  const [orgSeats, setOrgSeats] = useState(null)
  const [orgUsageStats, setOrgUsageStats] = useState(null)
  const [selectedOrgScopeId, setSelectedOrgScopeId] = useState('ALL')
  const [memberSearchTerm, setMemberSearchTerm] = useState('')
  const [memberPage, setMemberPage] = useState(1)
  const [activeTab, setActiveTab] = useState('dashboard')
  const [operatorOrgContracts, setOperatorOrgContracts] = useState([])
  const [operatorContractsLoading, setOperatorContractsLoading] = useState(false)
  const [operatorContractLoadError, setOperatorContractLoadError] = useState('')
  const [operatorOrgId, setOperatorOrgId] = useState(searchParams.get('orgId') || '')

  const operatorActiveOrgContracts = useMemo(
    () => normalizeActiveOrgContracts(operatorOrgContracts),
    [operatorOrgContracts],
  )

  const load = useCallback(async (cancelledRef = { current: false }) => {
    if (isOperator && !operatorOrgId) {
      if (!cancelledRef.current) {
        setOrgBilling(null)
        setOrgSeats(null)
        setOrgUsageStats(null)
        setLoading(false)
      }
      return
    }

    setLoading(true)
    setError('')
    try {
      const config = {
        __silent: true,
        __suppressErrorPageRedirect: true,
        ...(isOperator ? { params: { orgId: operatorOrgId } } : {}),
      }
      const billingRes = await api.get('/billing/org/me', config)
      if (cancelledRef.current) return
      const billing = billingRes?.data?.data ?? null
      setOrgBilling(billing)
      if (!isActiveOrgPaysContractValue(billing)) {
        setOrgSeats(null)
        setOrgUsageStats(null)
        return
      }
      const [seatsRes, statsRes] = await Promise.all([
        api.get('/billing/org/me/seats', config),
        api.get('/billing/org/me/usage-stats', config),
      ])
      if (cancelledRef.current) return
      setOrgSeats(seatsRes?.data?.data ?? null)
      setOrgUsageStats(statsRes?.data?.data ?? null)
    } catch (e) {
      if (!cancelledRef.current) {
        setError(getApiErrorMessage(e, '조직 대시보드를 불러오지 못했습니다.'))
      }
    } finally {
      if (!cancelledRef.current) setLoading(false)
    }
  }, [isOperator, operatorOrgId])

  useEffect(() => {
    if (!isOperator) {
      setOperatorOrgContracts([])
      setOperatorContractLoadError('')
      return undefined
    }

    let cancelled = false
    setOperatorContractsLoading(true)
    setOperatorContractLoadError('')
    api.get('/billing/admin/config/org-contracts', { __silent: true, __suppressErrorPageRedirect: true })
      .then((res) => {
        if (cancelled) return
        const payload = res?.data?.data ?? res?.data ?? []
        setOperatorOrgContracts(Array.isArray(payload) ? payload : [])
      })
      .catch((e) => {
        if (cancelled) return
        setOperatorOrgContracts([])
        setOperatorContractLoadError(getApiErrorMessage(e, '활성 조직계약 목록을 불러오지 못했습니다.'))
      })
      .finally(() => {
        if (!cancelled) setOperatorContractsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOperator])

  useEffect(() => {
    if (!isOperator || operatorContractsLoading) return
    const selectedExists = operatorActiveOrgContracts.some((contract) => String(contract.orgId) === String(operatorOrgId))
    const nextOrgId = selectedExists ? operatorOrgId : String(operatorActiveOrgContracts[0]?.orgId || '')
    if (nextOrgId !== operatorOrgId) setOperatorOrgId(nextOrgId)
  }, [isOperator, operatorActiveOrgContracts, operatorContractsLoading, operatorOrgId])

  useEffect(() => {
    if (!isOperator) return
    const next = new URLSearchParams(searchParams)
    if (operatorOrgId) next.set('orgId', operatorOrgId)
    else next.delete('orgId')
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [isOperator, operatorOrgId, searchParams, setSearchParams])

  useEffect(() => {
    if (!isOperator) return
    setSelectedOrgScopeId('ALL')
    setMemberSearchTerm('')
    setMemberPage(1)
  }, [isOperator, operatorOrgId])

  useEffect(() => {
    const cancelledRef = { current: false }
    load(cancelledRef)
    return () => { cancelledRef.current = true }
  }, [load])

  const contract = orgSeats?.contract || orgBilling?.contract || orgBilling || null
  const isActiveOrgPaysContract = isActiveOrgPaysContractValue(contract)
  const orgScopeOptions = useMemo(
    () => Array.isArray(orgSeats?.orgOptions) ? orgSeats.orgOptions : [],
    [orgSeats],
  )
  const orgMemberRows = useMemo(
    () => Array.isArray(orgSeats?.members) ? orgSeats.members : [],
    [orgSeats],
  )
  const scopedOrgMembers = selectedOrgScopeId === 'ALL'
    ? orgMemberRows
    : orgMemberRows.filter((member) => String(member.orgId) === String(selectedOrgScopeId))
  const memberSearchQuery = memberSearchTerm.trim().toLowerCase()
  const visibleOrgMembers = memberSearchQuery
    ? scopedOrgMembers.filter((member) => String(member?.name || '').toLowerCase().includes(memberSearchQuery))
    : scopedOrgMembers
  const memberTotalPages = Math.max(1, Math.ceil(visibleOrgMembers.length / MEMBER_PAGE_SIZE))
  const currentMemberPage = Math.min(memberPage, memberTotalPages)
  const memberPageStartIndex = (currentMemberPage - 1) * MEMBER_PAGE_SIZE
  const memberPageEndIndex = Math.min(memberPageStartIndex + MEMBER_PAGE_SIZE, visibleOrgMembers.length)
  const pagedOrgMembers = visibleOrgMembers.slice(memberPageStartIndex, memberPageEndIndex)
  const usageCategories = useMemo(() => collectUsageCategories(orgMemberRows), [orgMemberRows])
  const todayUsageWindow = getStatsWindow(orgUsageStats, 'today')
  const weekUsageWindow = getStatsWindow(orgUsageStats, 'last7Days') || getStatsWindow(orgUsageStats, 'last7')
  const monthUsageWindow = getStatsWindow(orgUsageStats, 'thisMonth')
  const monthLeadStats = monthUsageWindow?.leadStats || null
  const billableSeatCount = orgSeats?.billableSeatCount ?? contract?.contractedSeats ?? 0
  const approvedMemberCount = orgSeats?.scopeApprovedMemberCount ?? orgSeats?.approvedMemberCount ?? 0
  const availableSeatCount = Math.max(0, numberOrZero(billableSeatCount) - numberOrZero(approvedMemberCount))
  const seatUsageRate = formatPercent(approvedMemberCount, billableSeatCount)
  const todayActiveUserCount = todayUsageWindow?.activeUserCount ?? 0
  const weeklyActiveUserCount = weekUsageWindow?.activeUserCount ?? 0
  const monthlyActiveUserCount = monthUsageWindow?.activeUserCount ?? 0
  const monthlyActiveRate = formatPercent(monthlyActiveUserCount, approvedMemberCount)
  const weeklyInactiveMemberCount = Math.max(0, numberOrZero(approvedMemberCount) - numberOrZero(weeklyActiveUserCount))
  const exhaustedMemberCount = countMembersWithExhaustedQuota(scopedOrgMembers)
  const monthlyLeadCount = monthLeadStats?.totalLeadCount ?? 0
  const monthlyDoneLeadCount = monthLeadStats?.doneLeadCount ?? 0
  const monthlyUnassignedLeadCount = monthLeadStats?.unassignedLeadCount ?? 0
  const leadDoneRate = formatPercent(monthlyDoneLeadCount, monthlyLeadCount)
  const contractStartedAt = contract?.firstStartedAt || contract?.startedAt || contract?.effectiveFrom
  const contractStartedAtDisplay = contract?.firstStartedAtDisplay || contract?.startedAtDisplay || contract?.effectiveFromDisplay || formatDateOnly(contractStartedAt)
  const contractDurationLabel = formatContractDuration(contractStartedAt)
  const attentionCount = numberOrZero(exhaustedMemberCount)
    + numberOrZero(monthlyUnassignedLeadCount)
    + (numberOrZero(approvedMemberCount) > numberOrZero(billableSeatCount) ? 1 : 0)
  const usageTrendRows = buildPeriodTrendRows(todayUsageWindow, weekUsageWindow, monthUsageWindow, (window) => window?.totalEventCount)
  const activeTrendRows = buildPeriodTrendRows(todayUsageWindow, weekUsageWindow, monthUsageWindow, (window) => window?.activeUserCount)
  const leadTrendRows = buildPeriodTrendRows(todayUsageWindow, weekUsageWindow, monthUsageWindow, (window) => window?.leadStats?.totalLeadCount)
  const topMonthlyFeature = topFeatureStat(monthUsageWindow)
  const topMonthlyOrg = topOrgStat(monthUsageWindow)

  useEffect(() => {
    if (selectedOrgScopeId === 'ALL') return
    const selectedExists = orgScopeOptions.some((option) => String(option.orgId) === String(selectedOrgScopeId))
    if (!selectedExists) setSelectedOrgScopeId('ALL')
  }, [orgScopeOptions, selectedOrgScopeId])

  useEffect(() => {
    setMemberPage(1)
  }, [memberSearchTerm, selectedOrgScopeId])

  useEffect(() => {
    if (memberPage > memberTotalPages) setMemberPage(memberTotalPages)
  }, [memberPage, memberTotalPages])

  return (
    <section className="w-full">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">조직 대시보드</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isOperator && (
            <OperatorOrgContractSelector
              id="org-dashboard-operator-org"
              contracts={operatorActiveOrgContracts}
              value={operatorOrgId}
              onChange={setOperatorOrgId}
              loading={operatorContractsLoading}
            />
          )}
          <button
            type="button"
            onClick={() => load()}
            disabled={loading || (isOperator && !operatorOrgId)}
            className="inline-flex min-h-[2.5rem] items-center justify-center gap-2 rounded border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            새로고침
          </button>
        </div>
      </div>

      {loading && <div className="mt-8 text-slate-500">로딩 중...</div>}
      {!loading && error && (
        <div className="mt-8 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {!loading && !error && operatorContractLoadError && (
        <div className="mt-8 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{operatorContractLoadError}</div>
      )}
      {!loading && !error && !operatorContractLoadError && isOperator && !operatorOrgId && (
        <div className="mt-8 rounded border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-700">
          {operatorContractsLoading ? '활성 조직계약을 불러오는 중입니다.' : '조회할 활성 조직계약을 선택해 주세요.'}
        </div>
      )}
      {!loading && !error && !operatorContractLoadError && !(isOperator && !operatorOrgId) && !isActiveOrgPaysContract && (
        <div className="mt-8 rounded border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-700">
          현재 유효한 조직 계약이 없습니다.
        </div>
      )}

      {!loading && !error && !operatorContractLoadError && !(isOperator && !operatorOrgId) && isActiveOrgPaysContract && (
        <div className="mt-6 space-y-6">
          <OrgDashboardTabs activeTab={activeTab} onChange={setActiveTab} />

          {activeTab === 'dashboard' && (
            <div className="space-y-6" id="org-dashboard-tab-dashboard" role="tabpanel" aria-labelledby="org-dashboard-tab-button-dashboard">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <SummaryCard label="가입 조직원" value={`${formatNumber(approvedMemberCount)}명`} subValue={`${contract?.orgName || '조직'} · ${formatPlanLabel(contract?.appliedPlanCode) || '조직 계약'}`} />
                <SummaryCard label="활성 사용자" value={`${formatNumber(monthlyActiveUserCount)}명`} subValue={`활성률 ${monthlyActiveRate}`} />
                <SummaryCard label="이번달 이용" value={`${formatNumber(monthUsageWindow?.totalEventCount ?? 0)}건`} subValue={topMonthlyFeature ? `최다 ${topMonthlyFeature.featureName}` : '이번달 기준'} />
                <SummaryCard label="운영 점검" value={`${formatNumber(attentionCount)}건`} subValue={`한도주의 ${formatNumber(exhaustedMemberCount)}명 · 7일 비활성 ${formatNumber(weeklyInactiveMemberCount)}명`} />
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <DashboardTrendCard
                  label="이용 추이"
                  value={`${formatNumber(monthUsageWindow?.totalEventCount ?? 0)}건`}
                  subValue={`오늘 ${formatNumber(todayUsageWindow?.totalEventCount ?? 0)}건 · 7일 ${formatNumber(weekUsageWindow?.totalEventCount ?? 0)}건`}
                  rows={usageTrendRows}
                  unit="건"
                />
                <DashboardTrendCard
                  label="활성 사용자 추이"
                  value={`${formatNumber(monthlyActiveUserCount)}명`}
                  subValue={`오늘 ${formatNumber(todayActiveUserCount)}명 · 7일 ${formatNumber(weeklyActiveUserCount)}명`}
                  rows={activeTrendRows}
                  unit="명"
                />
                <DashboardTrendCard
                  label="리드 추이"
                  value={`${formatNumber(monthlyLeadCount)}건`}
                  subValue={`미배정 ${formatNumber(monthlyUnassignedLeadCount)}건 · 완료율 ${leadDoneRate}`}
                  rows={leadTrendRows}
                  unit="건"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
                <InsightCard
                  label="계약 이용 기간"
                  value={contractDurationLabel}
                  subValue={contractStartedAtDisplay ? `${contractStartedAtDisplay} 시작` : '최초 계약일 기준'}
                />
                <InsightCard
                  label="좌석 여유"
                  value={`${formatNumber(availableSeatCount)}명`}
                  subValue={`${formatNumber(approvedMemberCount)}명 / ${formatNumber(billableSeatCount)}명 · ${seatUsageRate}`}
                />
                <InsightCard
                  label="최다 이용 카테고리"
                  value={topMonthlyFeature?.featureName || '-'}
                  subValue={topMonthlyFeature ? `${formatNumber(topMonthlyFeature.eventCount ?? 0)}건 · 사용량 ${formatFeatureUsage(topMonthlyFeature)}` : '이번달 기준'}
                />
                <InsightCard
                  label="최다 이용 조직"
                  value={topMonthlyOrg?.name || '-'}
                  subValue={topMonthlyOrg ? `${formatNumber(topMonthlyOrg.value ?? 0)}건 · 이용자 ${formatNumber(topMonthlyOrg.activeUserCount ?? 0)}명` : '이번달 기준'}
                />
              </div>
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="space-y-6" id="org-dashboard-tab-stats" role="tabpanel" aria-labelledby="org-dashboard-tab-button-stats">
              <UsageStatsSection stats={orgUsageStats} orgOptions={orgScopeOptions} />
              <LeadStatsSection stats={orgUsageStats} />
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-6" id="org-dashboard-tab-details" role="tabpanel" aria-labelledby="org-dashboard-tab-button-details">
              <div className="rounded border bg-white p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold">조직원 이용 현황</h2>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => exportMemberUsageCsv(visibleOrgMembers, usageCategories)}
                      disabled={visibleOrgMembers.length === 0}
                      className="inline-flex min-h-[2.25rem] items-center justify-center gap-2 rounded border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Download className="h-4 w-4" aria-hidden="true" />
                      CSV
                    </button>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-600" htmlFor="org-dashboard-member-search">
                      이름 검색
                      <span className="relative">
                        <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                        <input
                          id="org-dashboard-member-search"
                          className="min-h-[2.25rem] w-44 rounded border border-slate-300 bg-white pl-8 pr-3 text-sm"
                          value={memberSearchTerm}
                          onChange={(e) => setMemberSearchTerm(e.target.value)}
                        />
                      </span>
                    </label>
                    {orgScopeOptions.length > 1 && (
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-slate-600" htmlFor="org-dashboard-scope">
                          조회 조직
                        </label>
                        <select
                          id="org-dashboard-scope"
                          className="min-h-[2.25rem] rounded border border-slate-300 bg-white px-3 text-sm"
                          value={selectedOrgScopeId}
                          onChange={(e) => setSelectedOrgScopeId(e.target.value)}
                        >
                          <option value="ALL">
                            전체 조직 ({formatNumber(orgSeats?.scopeApprovedMemberCount ?? orgSeats?.approvedMemberCount ?? orgMemberRows.length)}명)
                          </option>
                          {orgScopeOptions.map((option) => (
                            <option key={String(option.orgId)} value={String(option.orgId)}>
                              {option.orgName || '조직'} ({formatNumber(option.approvedMemberCount ?? 0)}명)
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-[48rem] text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="w-44 px-4 py-3 text-left">조직</th>
                        <th className="w-40 px-4 py-3 text-left">이름</th>
                        {usageCategories.length > 0 ? usageCategories.map((category) => (
                          <th key={category.key} className="w-40 px-4 py-3 text-left">{category.label}</th>
                        )) : (
                          <th className="w-40 px-4 py-3 text-left">사용/잔여</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {pagedOrgMembers.map((member, index) => (
                        <tr key={`${member.orgId || 'org'}-${member.email || member.name || index}`} className="border-t align-top">
                          <td className="px-4 py-3 font-medium">{member.orgName || '-'}</td>
                          <td className="px-4 py-3">{member.name || '-'}</td>
                          {usageCategories.length > 0 ? usageCategories.map((category) => (
                            <td key={category.key} className="px-4 py-3">
                              {formatUsageCell(findUsageItem(member.usageSummary, category))}
                            </td>
                          )) : (
                            <td className="px-4 py-3">{formatMemberUsageTotal(member)}</td>
                          )}
                        </tr>
                      ))}
                      {visibleOrgMembers.length === 0 && (
                        <tr>
                          <td className="px-4 py-8 text-center text-slate-500" colSpan={2 + Math.max(usageCategories.length, 1)}>
                            {memberSearchQuery ? '검색된 조직원이 없습니다.' : '표시할 조직원 이용 현황이 없습니다.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {visibleOrgMembers.length > 0 && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                    <div className="text-slate-500">
                      {formatNumber(memberPageStartIndex + 1)}-{formatNumber(memberPageEndIndex)} / {formatNumber(visibleOrgMembers.length)}명
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        title="이전 페이지"
                        aria-label="이전 페이지"
                        disabled={currentMemberPage <= 1}
                        onClick={() => setMemberPage((page) => Math.max(1, page - 1))}
                        className="inline-flex h-9 w-9 items-center justify-center rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <span className="min-w-[4.5rem] text-center font-medium text-slate-700">
                        {formatNumber(currentMemberPage)} / {formatNumber(memberTotalPages)}
                      </span>
                      <button
                        type="button"
                        title="다음 페이지"
                        aria-label="다음 페이지"
                        disabled={currentMemberPage >= memberTotalPages}
                        onClick={() => setMemberPage((page) => Math.min(memberTotalPages, page + 1))}
                        className="inline-flex h-9 w-9 items-center justify-center rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <ChevronRight className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

function OrgDashboardTabs({ activeTab, onChange }) {
  return (
    <div className="border-b border-slate-200" role="tablist" aria-label="조직 대시보드 보기">
      <div className="flex min-w-0 gap-1 overflow-x-auto">
        {ORG_DASHBOARD_TABS.map((tab) => {
          const active = tab.key === activeTab
          return (
            <button
              key={tab.key}
              id={`org-dashboard-tab-button-${tab.key}`}
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={`org-dashboard-tab-${tab.key}`}
              onClick={() => onChange(tab.key)}
              className={`min-h-[2.75rem] shrink-0 border-b-2 px-4 text-sm font-semibold ${active ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'}`}
            >
              {tab.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function DashboardTrendCard({ label, value, subValue, rows, unit }) {
  const safeRows = Array.isArray(rows) ? rows : []
  return (
    <div className="rounded border bg-white p-4">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-500">{label}</div>
          <div className="mt-2 text-xl font-semibold text-slate-900">{value}</div>
          <div className="mt-1 min-h-[1rem] text-xs text-slate-500">{subValue || '-'}</div>
        </div>
        <BarChart3 className="mt-1 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
      </div>
      <div className="mt-4 h-36">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={safeRows} margin={{ top: 8, right: 4, bottom: 0, left: 4 }}>
            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} stroke="#64748b" />
            <YAxis hide allowDecimals={false} domain={[0, 'dataMax']} />
            <Tooltip content={<DashboardTrendTooltip unit={unit} />} />
            <Bar dataKey="value" fill="#2563eb" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

function DashboardTrendTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload || {}
  return (
    <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold text-slate-900">{label || row.name || '-'}</div>
      <div className="mt-1 text-slate-600">{formatNumber(row.value ?? 0)}{unit || ''}</div>
    </div>
  )
}

function UsageStatsSection({ stats, orgOptions = [] }) {
  const windows = useMemo(() => Array.isArray(stats?.windows) ? stats.windows : [], [stats])
  const preferredWindowKey = windows.some((window) => String(window.key || '') === 'thisMonth')
    ? 'thisMonth'
    : String(windows[0]?.key || windows[0]?.label || '')
  const [selectedWindowKey, setSelectedWindowKey] = useState(preferredWindowKey)
  const [chartMode, setChartMode] = useState('categoryByOrg')
  const [selectedFeatureKey, setSelectedFeatureKey] = useState('')
  const [selectedStatsOrgId, setSelectedStatsOrgId] = useState('')
  const selectedWindow = windows.find((window) => String(window.key || window.label || '') === selectedWindowKey) || windows[0] || null
  const featureOptions = useMemo(() => collectStatsFeatures(selectedWindow), [selectedWindow])
  const statsOrgOptions = useMemo(() => collectStatsOrgs(selectedWindow, orgOptions), [selectedWindow, orgOptions])
  const chartRows = chartMode === 'categoryByOrg'
    ? buildCategoryByOrgRows(selectedWindow, selectedFeatureKey)
    : buildOrgByCategoryRows(selectedWindow, selectedStatsOrgId)
  const selectedFeature = featureOptions.find((feature) => feature.key === selectedFeatureKey) || featureOptions[0] || null
  const selectedStatsOrg = statsOrgOptions.find((org) => String(org.orgId) === String(selectedStatsOrgId)) || statsOrgOptions[0] || null
  const chartTitle = chartMode === 'categoryByOrg'
    ? `${selectedFeature?.label || '카테고리'} 조직별 이용`
    : `${selectedStatsOrg?.orgName || '조직'} 카테고리별 이용`

  useEffect(() => {
    if (windows.length === 0) return
    const selectedExists = windows.some((window) => String(window.key || window.label || '') === selectedWindowKey)
    if (!selectedExists) setSelectedWindowKey(preferredWindowKey)
  }, [preferredWindowKey, selectedWindowKey, windows])

  useEffect(() => {
    if (featureOptions.length === 0) {
      if (selectedFeatureKey) setSelectedFeatureKey('')
      return
    }
    if (!featureOptions.some((feature) => feature.key === selectedFeatureKey)) {
      setSelectedFeatureKey(featureOptions[0].key)
    }
  }, [featureOptions, selectedFeatureKey])

  useEffect(() => {
    if (statsOrgOptions.length === 0) {
      if (selectedStatsOrgId) setSelectedStatsOrgId('')
      return
    }
    if (!statsOrgOptions.some((org) => String(org.orgId) === String(selectedStatsOrgId))) {
      setSelectedStatsOrgId(String(statsOrgOptions[0].orgId))
    }
  }, [selectedStatsOrgId, statsOrgOptions])

  if (windows.length === 0) return null

  return (
    <div className="rounded border bg-white p-5">
      <h2 className="text-lg font-semibold">이용 통계</h2>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        {windows.map((window) => (
          <SummaryCard
            key={window.key || window.label}
            label={window.label || '기간'}
            value={`${formatNumber(window.totalEventCount ?? 0)}건`}
            subValue={`${formatNumber(window.activeUserCount ?? 0)}명 · 사용량 ${formatNumber(window.totalUsedUnits ?? 0)}`}
          />
        ))}
      </div>
      <div className="mt-5 border-t pt-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex overflow-hidden rounded border border-slate-300">
              {windows.map((window) => {
                const windowKey = String(window.key || window.label || '')
                const active = windowKey === selectedWindowKey
                return (
                  <button
                    key={windowKey}
                    type="button"
                    onClick={() => setSelectedWindowKey(windowKey)}
                    className={`min-h-[2.25rem] px-3 text-sm font-semibold ${active ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                  >
                    {window.label || '기간'}
                  </button>
                )
              })}
            </div>
            <div className="inline-flex overflow-hidden rounded border border-slate-300">
              <button
                type="button"
                onClick={() => setChartMode('categoryByOrg')}
                className={`min-h-[2.25rem] px-3 text-sm font-semibold ${chartMode === 'categoryByOrg' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              >
                카테고리별 조직 통계
              </button>
              <button
                type="button"
                onClick={() => setChartMode('orgByCategory')}
                className={`min-h-[2.25rem] px-3 text-sm font-semibold ${chartMode === 'orgByCategory' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              >
                조직별 카테고리 통계
              </button>
            </div>
          </div>
          {chartMode === 'categoryByOrg' ? (
            <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
              카테고리
              <select
                className="min-h-[2.25rem] rounded border border-slate-300 bg-white px-3 text-sm"
                value={selectedFeatureKey}
                onChange={(e) => setSelectedFeatureKey(e.target.value)}
              >
                {featureOptions.map((feature) => (
                  <option key={feature.key} value={feature.key}>{feature.label}</option>
                ))}
              </select>
            </label>
          ) : (
            <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
              조직
              <select
                className="min-h-[2.25rem] rounded border border-slate-300 bg-white px-3 text-sm"
                value={selectedStatsOrgId}
                onChange={(e) => setSelectedStatsOrgId(e.target.value)}
              >
                {statsOrgOptions.map((org) => (
                  <option key={String(org.orgId)} value={String(org.orgId)}>{org.orgName || '조직'}</option>
                ))}
              </select>
            </label>
          )}
        </div>
        <StatsBarChart title={chartTitle} rows={chartRows} />
      </div>
      <div className="mt-5 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-3 text-left">기간</th>
              <th className="px-4 py-3 text-left">카테고리</th>
              <th className="px-4 py-3 text-right">이용 건수</th>
              <th className="px-4 py-3 text-right">사용량</th>
              <th className="px-4 py-3 text-right">이용자</th>
            </tr>
          </thead>
          <tbody>
            {windows.flatMap((window) => {
              const rows = Array.isArray(window.featureStats) ? window.featureStats : []
              if (rows.length === 0) {
                return [(
                  <tr key={`${window.key || window.label}-empty`} className="border-t">
                    <td className="px-4 py-3 font-medium">{window.label || '-'}</td>
                    <td className="px-4 py-3 text-slate-500" colSpan={4}>집계된 이용 통계가 없습니다.</td>
                  </tr>
                )]
              }
              return rows.map((row, index) => (
                <tr key={`${window.key || window.label}-${row.featureCode || row.featureName || index}`} className="border-t">
                  <td className="px-4 py-3 font-medium">{index === 0 ? (window.label || '-') : ''}</td>
                  <td className="px-4 py-3">{row.featureName || '기타'}</td>
                  <td className="px-4 py-3 text-right">{formatNumber(row.eventCount ?? 0)}건</td>
                  <td className="px-4 py-3 text-right">{formatFeatureUsage(row)}</td>
                  <td className="px-4 py-3 text-right">{formatNumber(row.activeUserCount ?? 0)}명</td>
                </tr>
              ))
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function LeadStatsSection({ stats }) {
  const windows = useMemo(() => Array.isArray(stats?.windows) ? stats.windows : [], [stats])
  const preferredWindowKey = windows.some((window) => String(window.key || '') === 'thisMonth')
    ? 'thisMonth'
    : String(windows[0]?.key || windows[0]?.label || '')
  const [selectedWindowKey, setSelectedWindowKey] = useState(preferredWindowKey)
  const [chartMode, setChartMode] = useState('org')
  const selectedWindow = windows.find((window) => String(window.key || window.label || '') === selectedWindowKey) || windows[0] || null
  const leadStats = selectedWindow?.leadStats || null
  const orgRows = Array.isArray(leadStats?.orgLeadStats) ? leadStats.orgLeadStats : []
  const userRows = Array.isArray(leadStats?.userLeadStats) ? leadStats.userLeadStats : []
  const chartRows = chartMode === 'org'
    ? buildOrgLeadChartRows(orgRows)
    : buildUserLeadChartRows(userRows)
  const tableRows = chartMode === 'org' ? orgRows : userRows

  useEffect(() => {
    if (windows.length === 0) return
    const selectedExists = windows.some((window) => String(window.key || window.label || '') === selectedWindowKey)
    if (!selectedExists) setSelectedWindowKey(preferredWindowKey)
  }, [preferredWindowKey, selectedWindowKey, windows])

  if (windows.length === 0) return null

  return (
    <div className="rounded border bg-white p-5">
      <h2 className="text-lg font-semibold">리드 통계</h2>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        <SummaryCard
          label="리드"
          value={`${formatNumber(leadStats?.totalLeadCount ?? 0)}건`}
          subValue={selectedWindow?.label || '기간'}
        />
        <SummaryCard
          label="완료"
          value={`${formatNumber(leadStats?.doneLeadCount ?? 0)}건`}
          subValue={`완료율 ${formatPercent(leadStats?.doneLeadCount, leadStats?.totalLeadCount)}`}
        />
        <SummaryCard
          label="미배정"
          value={`${formatNumber(leadStats?.unassignedLeadCount ?? 0)}건`}
          subValue={`배정 ${formatNumber(leadStats?.assignedLeadCount ?? 0)}건`}
        />
      </div>
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded border border-slate-300 bg-white p-1">
          {windows.map((window) => {
            const value = String(window.key || window.label || '')
            const active = value === selectedWindowKey
            return (
              <button
                key={value}
                type="button"
                onClick={() => setSelectedWindowKey(value)}
                className={`min-h-[2rem] rounded px-3 text-sm font-medium ${active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                {window.label || '기간'}
              </button>
            )
          })}
        </div>
        <div className="inline-flex rounded border border-slate-300 bg-white p-1">
          <button
            type="button"
            onClick={() => setChartMode('org')}
            className={`min-h-[2rem] rounded px-3 text-sm font-medium ${chartMode === 'org' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            조직별
          </button>
          <button
            type="button"
            onClick={() => setChartMode('user')}
            className={`min-h-[2rem] rounded px-3 text-sm font-medium ${chartMode === 'user' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            개인별
          </button>
        </div>
      </div>
      <LeadStatsBarChart title={chartMode === 'org' ? '조직별 리드' : '개인별 리드'} rows={chartRows} />
      <div className="mt-5 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            {chartMode === 'org' ? (
              <tr>
                <th className="px-4 py-3 text-left">조직</th>
                <th className="px-4 py-3 text-right">리드</th>
                <th className="px-4 py-3 text-right">완료</th>
                <th className="px-4 py-3 text-right">미배정</th>
              </tr>
            ) : (
              <tr>
                <th className="px-4 py-3 text-left">이름</th>
                <th className="px-4 py-3 text-left">조직</th>
                <th className="px-4 py-3 text-right">리드</th>
                <th className="px-4 py-3 text-right">완료</th>
              </tr>
            )}
          </thead>
          <tbody>
            {tableRows.length === 0 ? (
              <tr className="border-t">
                <td className="px-4 py-8 text-center text-slate-500" colSpan={4}>집계된 리드가 없습니다.</td>
              </tr>
            ) : chartMode === 'org' ? tableRows.map((row, index) => (
              <tr key={`${row.orgId || row.orgName || index}`} className="border-t">
                <td className="px-4 py-3 font-medium">{row.orgName || '조직'}</td>
                <td className="px-4 py-3 text-right">{formatNumber(row.leadCount ?? 0)}건</td>
                <td className="px-4 py-3 text-right">{formatNumber(row.doneLeadCount ?? 0)}건</td>
                <td className="px-4 py-3 text-right">{formatNumber(row.unassignedLeadCount ?? 0)}건</td>
              </tr>
            )) : tableRows.map((row, index) => (
              <tr key={`${row.userId || row.userName || 'unassigned'}-${row.orgId || index}`} className="border-t">
                <td className="px-4 py-3 font-medium">{row.userName || '미배정'}</td>
                <td className="px-4 py-3">{row.orgName || '-'}</td>
                <td className="px-4 py-3 text-right">{formatNumber(row.leadCount ?? 0)}건</td>
                <td className="px-4 py-3 text-right">{formatNumber(row.doneLeadCount ?? 0)}건</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function StatsBarChart({ title, rows }) {
  return (
    <div className="mt-4">
      <div className="mb-3 flex min-w-0 items-center gap-2">
        <BarChart3 className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
        <h3 className="min-w-0 truncate text-sm font-bold text-slate-900">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded border border-dashed border-slate-200 text-sm text-slate-500">
          집계된 이용 통계가 없습니다.
        </div>
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 104 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
              <XAxis type="number" allowDecimals={false} tickFormatter={formatCompactNumber} tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis
                type="category"
                dataKey="name"
                width={104}
                tickFormatter={truncateChartLabel}
                tick={{ fontSize: 11 }}
                stroke="#64748b"
              />
              <Tooltip content={<StatsChartTooltip />} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {rows.map((row, index) => (
                  <Cell key={`${row.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

function StatsChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload || {}
  return (
    <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold text-slate-900">{label || row.name || '-'}</div>
      <div className="mt-1 text-slate-600">이용 건수 {formatNumber(row.value ?? 0)}건</div>
      <div className="text-slate-600">사용량 {formatFeatureUsage(row)}</div>
      <div className="text-slate-600">이용자 {formatNumber(row.activeUserCount ?? 0)}명</div>
    </div>
  )
}

function LeadStatsBarChart({ title, rows }) {
  return (
    <div className="mt-4">
      <div className="mb-3 flex min-w-0 items-center gap-2">
        <BarChart3 className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
        <h3 className="min-w-0 truncate text-sm font-bold text-slate-900">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded border border-dashed border-slate-200 text-sm text-slate-500">
          집계된 리드가 없습니다.
        </div>
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 104 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
              <XAxis type="number" allowDecimals={false} tickFormatter={formatCompactNumber} tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis
                type="category"
                dataKey="name"
                width={104}
                tickFormatter={truncateChartLabel}
                tick={{ fontSize: 11 }}
                stroke="#64748b"
              />
              <Tooltip content={<LeadStatsChartTooltip />} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {rows.map((row, index) => (
                  <Cell key={`${row.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

function LeadStatsChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload || {}
  return (
    <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold text-slate-900">{label || row.name || '-'}</div>
      <div className="mt-1 text-slate-600">리드 {formatNumber(row.value ?? 0)}건</div>
      <div className="text-slate-600">완료 {formatNumber(row.doneLeadCount ?? 0)}건</div>
      <div className="text-slate-600">미배정 {formatNumber(row.unassignedLeadCount ?? 0)}건</div>
    </div>
  )
}

function collectStatsFeatures(window) {
  const map = new Map()
  const rows = [
    ...(Array.isArray(window?.featureStats) ? window.featureStats : []),
    ...(Array.isArray(window?.orgFeatureStats) ? window.orgFeatureStats : []),
  ]
  rows.forEach((row) => {
    const key = statsFeatureKey(row)
    if (!key || map.has(key)) return
    map.set(key, {
      key,
      label: row.featureName || '기타',
      quotaUnit: row.quotaUnit,
    })
  })
  return Array.from(map.values())
}

function collectStatsOrgs(window, orgOptions) {
  const map = new Map()
  ;(Array.isArray(orgOptions) ? orgOptions : []).forEach((org) => {
    if (org?.orgId == null) return
    map.set(String(org.orgId), {
      orgId: String(org.orgId),
      orgName: org.orgName || '조직',
    })
  })
  ;(Array.isArray(window?.orgFeatureStats) ? window.orgFeatureStats : []).forEach((row) => {
    if (row?.orgId == null) return
    const key = String(row.orgId)
    if (map.has(key)) return
    map.set(key, {
      orgId: key,
      orgName: row.orgName || '조직',
    })
  })
  return Array.from(map.values())
}

function buildCategoryByOrgRows(window, selectedFeatureKey) {
  const rows = Array.isArray(window?.orgFeatureStats) ? window.orgFeatureStats : []
  return mergeChartRows(
    rows.filter((row) => statsFeatureKey(row) === selectedFeatureKey),
    (row) => String(row.orgId ?? row.orgName ?? ''),
    (row) => row.orgName || '조직',
  )
}

function buildOrgByCategoryRows(window, selectedStatsOrgId) {
  const rows = Array.isArray(window?.orgFeatureStats) ? window.orgFeatureStats : []
  return mergeChartRows(
    rows.filter((row) => String(row.orgId) === String(selectedStatsOrgId)),
    (row) => statsFeatureKey(row),
    (row) => row.featureName || '기타',
  )
}

function buildOrgLeadChartRows(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      key: String(row.orgId ?? row.orgName ?? ''),
      name: row.orgName || '조직',
      value: numberOrZero(row.leadCount),
      doneLeadCount: numberOrZero(row.doneLeadCount),
      assignedLeadCount: numberOrZero(row.assignedLeadCount),
      unassignedLeadCount: numberOrZero(row.unassignedLeadCount),
    }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value || String(a.name).localeCompare(String(b.name), 'ko-KR'))
    .slice(0, 12)
}

function buildUserLeadChartRows(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      key: `${row.userId || row.userName || 'unassigned'}-${row.orgId || ''}`,
      name: row.userName || '미배정',
      value: numberOrZero(row.leadCount),
      doneLeadCount: numberOrZero(row.doneLeadCount),
      assignedLeadCount: numberOrZero(row.assignedLeadCount),
      unassignedLeadCount: numberOrZero(row.unassignedLeadCount),
    }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value || String(a.name).localeCompare(String(b.name), 'ko-KR'))
    .slice(0, 12)
}

function mergeChartRows(rows, keyOf, nameOf) {
  const map = new Map()
  ;(Array.isArray(rows) ? rows : []).forEach((row) => {
    const key = keyOf(row)
    if (!key) return
    const current = map.get(key) || {
      key,
      name: nameOf(row),
      value: 0,
      usedUnits: 0,
      activeUserCount: 0,
      quotaUnit: row.quotaUnit,
    }
    current.value += numberOrZero(row.eventCount)
    current.usedUnits += numberOrZero(row.usedUnits)
    current.activeUserCount += numberOrZero(row.activeUserCount)
    if (!current.quotaUnit && row.quotaUnit) current.quotaUnit = row.quotaUnit
    map.set(key, current)
  })
  return Array.from(map.values())
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value || b.usedUnits - a.usedUnits || String(a.name).localeCompare(String(b.name), 'ko-KR'))
    .slice(0, 12)
}

function statsFeatureKey(row) {
  return String(row?.featureCode || row?.featureName || '').trim()
}

function truncateChartLabel(value) {
  const text = String(value || '')
  return text.length > 14 ? `${text.slice(0, 13)}...` : text
}

function formatCompactNumber(value) {
  const number = Number(value || 0)
  if (!Number.isFinite(number)) return '0'
  if (Math.abs(number) >= 10000) {
    return `${(number / 10000).toLocaleString('ko-KR', { maximumFractionDigits: 1 })}만`
  }
  return number.toLocaleString('ko-KR')
}

function SummaryCard({ label, value, subValue = null }) {
  return (
    <div className="rounded border bg-white p-4">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="mt-2 text-xl font-semibold">{value}</div>
      {subValue && <div className="mt-1 text-xs text-slate-500">{subValue}</div>}
    </div>
  )
}

function InsightCard({ label, value, subValue }) {
  return (
    <div className="rounded border bg-white p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 min-w-0 truncate text-lg font-bold text-slate-900">{value}</div>
      <div className="mt-1 min-h-[1rem] text-xs text-slate-500">{subValue || '-'}</div>
    </div>
  )
}

function isActiveOrgPaysContractValue(contract) {
  const billingMode = contract?.billingMode || contract?.billingModel
  return Boolean(contract?.currentlyEffective) && String(billingMode || '').toUpperCase() === 'ORG_PAYS'
}

function getStatsWindow(stats, key) {
  const windows = Array.isArray(stats?.windows) ? stats.windows : []
  return windows.find((window) => String(window.key || '') === key) || null
}

function buildPeriodTrendRows(todayWindow, weekWindow, monthWindow, valueOf) {
  return [
    { name: '오늘', value: numberOrZero(valueOf(todayWindow)) },
    { name: '7일', value: numberOrZero(valueOf(weekWindow)) },
    { name: '이번달', value: numberOrZero(valueOf(monthWindow)) },
  ]
}

function formatContractDuration(value) {
  const startedAt = parseDateValue(value)
  if (!startedAt) return '-'
  const now = new Date()
  let months = (now.getFullYear() - startedAt.getFullYear()) * 12 + (now.getMonth() - startedAt.getMonth())
  if (now.getDate() < startedAt.getDate()) months -= 1
  if (months < 1) return '1개월 미만'
  if (months < 12) return `${formatNumber(months)}개월째`
  const years = Math.floor(months / 12)
  const restMonths = months % 12
  return restMonths > 0
    ? `${formatNumber(years)}년 ${formatNumber(restMonths)}개월째`
    : `${formatNumber(years)}년째`
}

function formatDateOnly(value) {
  const date = parseDateValue(value)
  if (!date) return ''
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

function parseDateValue(value) {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function formatPercent(value, total) {
  const n = numberOrZero(value)
  const d = numberOrZero(total)
  if (d <= 0) return '-'
  return `${Math.round((n / d) * 100)}%`
}

function topFeatureStat(window) {
  const rows = Array.isArray(window?.featureStats) ? window.featureStats : []
  return rows
    .filter((row) => numberOrZero(row?.eventCount) > 0)
    .sort((a, b) => numberOrZero(b.eventCount) - numberOrZero(a.eventCount) || numberOrZero(b.usedUnits) - numberOrZero(a.usedUnits))[0] || null
}

function topOrgStat(window) {
  const rows = mergeChartRows(
    Array.isArray(window?.orgFeatureStats) ? window.orgFeatureStats : [],
    (row) => String(row.orgId ?? row.orgName ?? ''),
    (row) => row.orgName || '조직',
  )
  return rows[0] || null
}

function countMembersWithExhaustedQuota(members) {
  return (Array.isArray(members) ? members : []).filter((member) => (
    (Array.isArray(member?.usageSummary) ? member.usageSummary : []).some((item) => {
      const quotaMode = String(item?.quotaMode || '').toUpperCase()
      if (quotaMode === 'UNLIMITED') return false
      const remaining = numberOrNull(item?.remainingValue ?? item?.remaining)
      return remaining != null && remaining <= 0
    })
  )).length
}

function collectUsageCategories(members) {
  const map = new Map()
  ;(members || []).forEach((member) => {
    ;(Array.isArray(member?.usageSummary) ? member.usageSummary : []).forEach((item) => {
      const key = usageItemKey(item)
      if (!key || map.has(key)) return
      map.set(key, {
        key,
        label: item.featureName || '기타',
        quotaUnit: item.quotaUnit,
      })
    })
  })
  return Array.from(map.values())
}

function usageItemKey(item) {
  return String(item?.featureCode || item?.featureName || '').trim()
}

function findUsageItem(items, category) {
  const rows = Array.isArray(items) ? items : []
  return rows.find((item) => usageItemKey(item) === category.key) || null
}

function formatUsageCell(item) {
  if (!item) return '-'
  const unit = quotaUnitLabel(item.quotaUnit)
  const used = numberOrZero(item.usedValue ?? item.used)
  const quotaMode = String(item.quotaMode || '').toUpperCase()
  if (quotaMode === 'UNLIMITED') {
    return `${formatNumber(used)}${unit} / 무제한`
  }
  const remaining = numberOrNull(item.remainingValue ?? item.remaining)
  return `${formatNumber(used)}${unit} / ${remaining == null ? '-' : `${formatNumber(remaining)}${unit}`}`
}

function formatMemberUsageTotal(member) {
  const used = numberOrZero(member?.totalUsedValue)
  const remaining = numberOrNull(member?.totalRemainingValue)
  return `${formatNumber(used)} / ${remaining == null ? '-' : formatNumber(remaining)}`
}

function formatFeatureUsage(row) {
  const unit = quotaUnitLabel(row?.quotaUnit)
  return `${formatNumber(row?.usedUnits ?? 0)}${unit}`
}

function exportMemberUsageCsv(members, categories) {
  if (!Array.isArray(members) || members.length === 0) return
  const safeCategories = Array.isArray(categories) ? categories : []
  const headers = ['조직', '이름', ...(safeCategories.length > 0 ? safeCategories.map((category) => `${category.label} 사용/잔여`) : ['사용/잔여'])]
  const rows = members.map((member) => [
    member?.orgName || '',
    member?.name || '',
    ...(safeCategories.length > 0
      ? safeCategories.map((category) => formatUsageCell(findUsageItem(member?.usageSummary, category)))
      : [formatMemberUsageTotal(member)]),
  ])
  const csv = [headers, ...rows]
    .map((row) => row.map(csvCell).join(','))
    .join('\r\n')
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `org_dashboard_members_${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function csvCell(value) {
  const text = String(value ?? '')
  return `"${text.replace(/"/g, '""')}"`
}

function formatPlanLabel(value) {
  const normalized = String(value || '').toUpperCase()
  if (normalized === 'BASIC') return '베이직'
  if (normalized === 'STANDARD') return '스탠다드'
  if (normalized === 'PREMIUM') return '프리미엄'
  if (normalized === 'ENTERPRISE') return '엔터프라이즈'
  return ''
}

function quotaUnitLabel(quotaUnit) {
  switch (quotaUnit) {
    case 'CASE':
    case 'COUNT':
      return '건'
    case 'RECIPIENT':
      return '명'
    case 'RUN':
      return '회'
    default:
      return ''
  }
}

function numberOrZero(value) {
  const number = typeof value === 'number' ? value : Number(value || 0)
  return Number.isFinite(number) ? number : 0
}

function numberOrNull(value) {
  if (value == null || value === '') return null
  const number = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(number) ? number : null
}

function formatNumber(value) {
  const number = typeof value === 'number' && Number.isFinite(value) ? value : Number(value || 0)
  return number.toLocaleString('ko-KR')
}
